import BpkPriceRange from '@skyscanner/backpack-web/bpk-component-price-range';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import type { PriceLevel } from '@shared/types/frontend/ubff/price-band';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './AtypicalDealsContent.module.scss';

import {
  TRANSLATE_MAPPING,
  TRANSLATE_NEW_MAPPING,
} from '@hotels-hotel-detail/utils/atypical-deals';
import { useHotelPriceBand } from '@shared/data-access-frontend/hooks/useAtypicalDealsContext';
import AtypicalDealsDescription from '@shared/ui/components/AtypicalDealsDescription';
import AtypicalDealsTitle from '@shared/ui/components/AtypicalDealsTitle';
import FormattedMessage from '@shared/ui/components/i18n/FormattedMessage';
import { withI18n } from '@shared/ui/contexts/i18n';
import { getPercentAve } from '@shared/utils-frontend/price-range';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  hotelName: string;
  type: PriceLevel;
  lowestPrice: number;
  isMobile?: boolean;
};

const AtypicalDealsContent = ({
  hotelName,
  i18n: { formatCurrency, translate },
  isMobile,
  lowestPrice,
  type,
}: Props) => {
  const priceBand = useHotelPriceBand();

  if (!priceBand) {
    return null;
  }

  const { pHigh, pLow, pOutlierHigh, pOutlierLow, wideMonth } = priceBand;
  const indicativePercent = getPercentAve({
    indicativePrice: lowestPrice,
    highPrice: pHigh,
    lowPrice: pLow,
    outlierHigh: pOutlierHigh,
    outlierLow: pOutlierLow,
    type,
  });

  const title = (
    <AtypicalDealsTitle
      type={type}
      labelKey={
        isMobile ? TRANSLATE_MAPPING[type] : TRANSLATE_NEW_MAPPING[type]
      }
    />
  );

  return (
    <>
      <div className={cls('AtypicalDealsContent__title')}>{title}</div>
      <div className={cls('AtypicalDealsContent')}>
        <div className={cls('AtypicalDealsContent__left')}>
          <div className={cls('AtypicalDealsContent__leftTitle')}>{title}</div>
          <AtypicalDealsDescription
            isFromDV={false}
            explanation={translate('hd_price_range_description_explanation')}
            description={
              <FormattedMessage
                label={
                  type === 'PRICE_LEVEL_HIGH'
                    ? 'hd_price_range_desc_for_higher'
                    : 'hd_price_range_description'
                }
                data={{
                  hotel: hotelName,
                  lowest_price: formatCurrency(lowestPrice),
                  month: wideMonth,
                  low_price: formatCurrency(pLow.price),
                  high_price: formatCurrency(pHigh.price),
                }}
                strong
              />
            }
          />
        </div>
        <div className={cls('AtypicalDealsContent__priceRange')}>
          <BpkPriceRange
            marker={{
              price: formatCurrency(lowestPrice),
              percentage: indicativePercent,
            }}
            segments={{
              low: {
                price: formatCurrency(pLow.price),
                percentage: pLow.percentile,
              },
              high: {
                price: formatCurrency(pHigh.price),
                percentage: pHigh.percentile,
              },
            }}
          />
        </div>
      </div>
    </>
  );
};

export default withI18n(AtypicalDealsContent);
